/**
 * Bridges `viewer-forbidden` events from the API service into a single
 * deduped toast. Mount once at the app root.
 *
 * Why a bridge: a bulk operation that fires N parallel requests would
 * otherwise raise N identical toasts. We coalesce events that arrive
 * within a short window into one toast.
 */

import { useEffect } from 'react';
import { subscribeViewerForbidden } from '../../services/events/viewerForbiddenEvents';
import { useAppToast } from '../../hooks/ui/useAppToast';

const DEDUPE_WINDOW_MS = 1000;

export function ViewerForbiddenToastBridge(): null {
  const toast = useAppToast();

  useEffect(() => {
    let lastShownAt = 0;
    return subscribeViewerForbidden((detail) => {
      // Dedupe parallel rejections from the same operation.
      const now = Date.now();
      if (now - lastShownAt < DEDUPE_WINDOW_MS) return;
      lastShownAt = now;

      toast({
        title: 'View-only workspace',
        description:
          detail.message ??
          'Viewers cannot modify workspace data. Ask a workspace admin if you need write access.',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
    });
  }, [toast]);

  return null;
}
